import { AnimatePresence, MotiProps, MotiView } from 'moti';
import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { InteractionManager } from 'react-native';
import {
  PanGestureHandler,
  PanGestureHandlerGestureEvent,
} from 'react-native-gesture-handler';
import {
  Easing,
  interpolate,
  runOnJS,
  useAnimatedGestureHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { BehaviorSubject } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { css } from 'styled-components';
import styled from 'styled-components/native';
import { isDebugEnabled } from '../../functions/debugging';
import useBehaviorSubject from '../../functions/hooks/useBehaviorSubject';
import { usePrevious } from '../../functions/hooks/usePrevious';
import { makeDebug } from '../../utils/makeDebug';
import { Shadow1Style } from '../styles/Colors';
import { useIsMobileLayout } from '../styles/responsive';
import { SnackbarStyles, useSnackbarStylePresets } from './styles-presets';

const debug = makeDebug('snackbar', isDebugEnabled('snackbar'));

export type SnackbarContent = {
  delay?: number;
  /** auto dismiss timeout (ms) of the bar */
  dismiss?: number;
  /** allow customize the look & feel of the bar */
  styles?: SnackbarStyles;
  /** allow responding to bar press */
  onPress?: () => void;
  onDismiss?: () => void;
  children?: ReactNode;
};

const globalSnackbar$ = new BehaviorSubject<SnackbarContent | undefined>(
  undefined,
);

const ContentContainer = styled.TouchableOpacity`
  ${Shadow1Style}
`;

export const WiredSnackbar = memo(() => {
  const [content, setContent] = useBehaviorSubject(globalSnackbar$);
  const prev = usePrevious(content);
  if (prev && !content) {
    debug('snackbar dismissed');
  }

  useEffect(() => {
    if (content == null) {
      return;
    }
    const timer = setTimeout(() => {
      setContent(undefined);
    }, content.dismiss ?? 3000);
    return () => clearTimeout(timer);
  }, [content, setContent]);

  return (
    <AnimatePresence>
      {content != null && (
        <Snackbar {...content} onDismiss={() => setContent(undefined)} />
      )}
    </AnimatePresence>
  );
});

const Snackbar = (props: SnackbarContent) => {
  // BUG!!! onDismiss is not passed to children actually....
  const count = Children.count(props.children);
  if (count === 1) {
    const child = Children.only(props.children);
    if (isValidElement(child)) {
      const el = cloneElement(child as any, {
        onDismiss: props.onDismiss,
        onPress: child.props.onPress ?? props.onPress,
      });
      return <>{el}</>;
    }
  }

  return <>{props.children}</>;
};

export const SnackbarBase = ({ styles, ...props }: SnackbarContent) => {
  const presets = useSnackbarStylePresets();
  const { top, left, right } = useSafeAreaInsets();
  const isMobile = useIsMobileLayout();
  const [exitAnimation, setExitAnimation] = useState<MotiProps['exit']>({
    translateY: -50,
    opacity: 0,
  });
  const { onDismiss } = props;

  const translateY = useSharedValue(0);
  const opacity = useDerivedValue(() =>
    interpolate(translateY.value, [-50, 0], [0, 1]),
  );

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [
      {
        translateY: translateY.value,
      },
    ],
  }));

  const handleGestureDismiss = useCallback(() => {
    debug('dismissed by swipe gesture');
    onDismiss?.();
  }, [onDismiss]);

  const handler = useAnimatedGestureHandler<
    PanGestureHandlerGestureEvent,
    { translateY: number; startPosY: number; activated: boolean }
  >({
    onStart(event, context) {
      context.translateY = 0;
      context.startPosY = event.absoluteY;
      context.activated = false;
    },
    onActive(event, context) {
      if (event.absoluteY > context.startPosY) {
        return;
      }
      context.activated = true;
      translateY.value = event.translationY;
    },
    onEnd(event, context) {
      if (!context.activated || -event.translationY < 10) {
        translateY.value = 0;
        return;
      }
      runOnJS(setExitAnimation)(undefined);
      translateY.value = withTiming(
        -50,
        {
          duration: 350,
          easing: Easing.in(Easing.linear),
        },
        () => {
          runOnJS(handleGestureDismiss)();
        },
      );
    },
  });

  return (
    <PanGestureHandler onGestureEvent={handler}>
      <MotiView
        pointerEvents={'box-none'}
        transition={
          exitAnimation
            ? { type: 'timing', duration: 300, delay: props.delay }
            : undefined
        }
        from={exitAnimation ? { translateY: -50, opacity: 0 } : undefined}
        animate={exitAnimation ? { translateY: 0, opacity: 1 } : undefined}
        exit={exitAnimation}
        css={`
          position: absolute;
          right: ${isMobile ? right + 15 : right + 100}px;
          top: ${isMobile ? top + 5 : top + 50}px;
          flex-direction: row;
          justify-content: flex-end;
          background-color: #fff;
          border-radius: 10px;
          max-width: 400px;
          ${css`
            ${isMobile ? `left: ${left + 15}px;` : ''}
          `};
        `}
        style={[presets.default.bar, styles?.bar, animatedStyle]}
      >
        <ContentContainer
          css={`
            flex: 1;
            max-width: 400px;
            border-radius: 10px;
            overflow: hidden;
          `}
          style={[styles?.contentContainer]}
          activeOpacity={props.onPress ? undefined : 1}
          onPress={
            props.onPress
              ? () => {
                  props.onDismiss?.();
                  debug('user pressed snack bar');
                  void InteractionManager.runAfterInteractions(() => {
                    setTimeout(() => {
                      props.onPress?.();
                    }, 100);
                  });
                }
              : undefined
          }
        >
          {props.children}
        </ContentContainer>
      </MotiView>
    </PanGestureHandler>
  );
};

export const presentSnackbar = (options: SnackbarContent) => {
  return globalSnackbar$
    .pipe(
      filter(value => value == null),
      first(),
    )
    .subscribe({
      next() {
        globalSnackbar$.next(options);
      },
    });
};

export default Snackbar;
